import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { observable, toJS } from 'mobx';
import { Alert, Intent } from '@blueprintjs/core';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import Toast from 'helpers/toaster';
import { currencyFormattedAmount } from 'helpers/currency';
import * as DataList from 'components/DataList';

// Layout shared by the list and its loading skeleton (pages/service_types/index.js).
export const SERVICE_TYPE_COLUMNS = [
  { area: 'name', label: 'Name', width: 'minmax(180px, 1.2fr)', bone: 'single' },
  { area: 'description', label: 'Description', width: 'minmax(220px, 2fr)', bone: 'single', icon: 'align-left' },
  { area: 'rate', label: 'Rate', width: '120px', bone: 'single' },
  { area: 'actions', label: '', width: '104px', bone: 'none' },
];

export const SERVICE_TYPE_CARDS = {
  columns: 'minmax(0, 1fr) auto',
  areas: ['name actions', 'description description', 'rate rate'],
  footer: ['rate'],
};

export const SERVICE_TYPE_MIN_TABLE_WIDTH = 760;

const hasAmount = (value) => value !== null && value !== undefined && value !== '';

@inject('store', 'clientStore', 'serviceTypeStore', 'viewStore') @observer
class ServiceTypesTable extends Component {
  @observable isOpenAddEquipmentDialog = false;
  @observable isDeleteDialogVisible = false;
  @observable service;

  onPaginationChange(newPage) {
    let {
      viewStore: {
        serviceTypeSearch
      }
    } = this.props;
    this.props.viewStore.serviceTypeSearch = {
      ...serviceTypeSearch,
      page: newPage
    };
    this.props.viewStore.serviceTypes();
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.serviceTypeStore.deleteServiceType(this.service)
      .then(this.onSuccessfulDeleteServiceType.bind(this))
      .catch(this.onFailDeleteServiceType.bind(this))
  }

  onSuccessfulDeleteServiceType(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Service type deleted successfully!', intent: Intent.SUCCESS })
    this.service = null;
    this.isDeleteDialogVisible = false;
  }

  onFailDeleteServiceType(error) {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Unable to delete service type!', intent: Intent.DANGER })
  }

  handleServiceTypeClick(event, service) {
    switch (event.label) {
      case 'Edit':
        this.service = service;
        this.props.viewStore.serviceTypeShow({uuid: service.attributes.id})
        break;
      case 'Delete':
        this.isDeleteDialogVisible = true;
        this.service = service;
        break;
      default:
    }
  }

  copyServiceType(service) {
    let {
      props: {
        serviceTypeStore: {
          newServiceType: serviceTypeDetail
        }
      }
    } = this;

    // A copy of the row in the Add form's slot (not the row itself, and not the
    // service type page's record), sent as a new service type.
    serviceTypeDetail.object = toJS(service);
    serviceTypeDetail.object.id = '';
    serviceTypeDetail.object.service_type_pricing_parameters_attributes = [];
    serviceTypeDetail.object.service_frequencies_attributes = [];
    if(serviceTypeDetail.object.service_type_pricing_parameters){
      serviceTypeDetail.object.service_type_pricing_parameters && serviceTypeDetail.object.service_type_pricing_parameters.map((values) => {
        return(
          serviceTypeDetail.object.service_type_pricing_parameters_attributes.push({
            service_type_id:values.service_type_id,
            pricing_parameter_id:values.pricing_parameter_id,
            label:values.label
          })
        )
      })
    }

    if(serviceTypeDetail.object.service_frequencies){
      serviceTypeDetail.object.service_frequencies && serviceTypeDetail.object.service_frequencies.map((values) => {
        return(
          serviceTypeDetail.object.service_frequencies_attributes.push({
            frequency_id:values.frequency_id,
            service_type_id:values.service_type_id,
            label:values.label
          })
        )
      })
    }

    this.props.serviceTypeStore
      .createServiceType()
      .then(response => {
        Toast.show({
          icon: "thumbs-up",
          message: "Service type copied successfully!",
          intent: Intent.SUCCESS
        });
      })
      .catch(error => {
        Toast.show({
          icon: "warning-sign",
          message: "Unable to copied service type!",
          intent: Intent.DANGER
        });
      });
    

  }

  // Empty list's "Clear search": back to all service types, page 1.
  clearSearch() {
    const { viewStore } = this.props;
    viewStore.serviceTypeSearch = { ...viewStore.serviceTypeSearch, term: '*', page: 1 };
    viewStore.bumpListFilters();
    viewStore.serviceTypes();
  }

  render() {
    let {
      store,
      viewStore,
      serviceTypeStore: {
        serviceType: {
          objects,
          meta
        }
      }
    } = this.props;
    const { serviceTypeSearch } = viewStore;
    const term = serviceTypeSearch.term && serviceTypeSearch.term !== '*' ? serviceTypeSearch.term : '';

    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="service types"
          term={term}
          onClearFilters={this.clearSearch.bind(this)}
          extraAction={!term && { label: 'Add service type', icon: 'plus', onClick: () => { store.isOpenAddServiceTypeDialog = true; }, primary: true }}
        />
      );
    }
    return (
      <React.Fragment>
        <Footer>
          <div className="bp3-navbar-group bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
        <DataList.List
          columns={SERVICE_TYPE_COLUMNS}
          cards={SERVICE_TYPE_CARDS}
          minTableWidth={SERVICE_TYPE_MIN_TABLE_WIDTH}
          total={meta.total_records || objects.length}
          noun="service types"
          note={DataList.sortNote(serviceTypeSearch.order)}
        >
          {objects.map((services) => {
            const attributes = services.attributes;
            return (
              <DataList.Row key={services.id}>
                <DataList.Cell area="name">
                  <DataList.Lines
                    title={attributes.name}
                    primary={
                      <DataList.RowLink
                        href={`/dashboard/service-type/${attributes.id}/show`}
                        label={`Service type ${attributes.name}`}
                      >
                        {attributes.name}
                      </DataList.RowLink>
                    }
                  />
                </DataList.Cell>
                <DataList.Cell area="description">
                  {attributes.description ? (
                    <DataList.Lines primary={attributes.description} strong={false} />
                  ) : (
                    <DataList.Muted>No description</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="rate">
                  {hasAmount(attributes.rate) ? (
                    <span className="dl-lines__primary">
                      <DataList.CardOnly>Rate </DataList.CardOnly>
                      {currencyFormattedAmount(attributes.rate)}
                    </span>
                  ) : (
                    <DataList.Muted>—</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'duplicate', label: 'Duplicate service type', onClick: () => this.copyServiceType(attributes) },
                      { icon: 'edit', label: 'Edit service type', onClick: () => viewStore.serviceTypeShow({ uuid: attributes.id }) },
                      { icon: 'trash', label: 'Delete service type', intent: 'danger', onClick: () => this.handleServiceTypeClick({ label: 'Delete' }, services) },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>
            Warnning! Deleting this service type will not be recoverable.
          </p>
          <p>
            Are you sure you want to do this?
          </p>
        </Alert>
        
        <Footer>
          <div className="bp3-navbar-group bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
        
      </React.Fragment>
    )
  }

}

export default ServiceTypesTable;